'use client';

import { useAuth, type UserRole } from '@/hooks/useAuth';

interface RoleGateProps {
  /**
   * Roles that are allowed to see the children.
   * User must have at least one of these roles.
   */
  allowedRoles: UserRole[];
  
  /**
   * Content to render if user has required role
   */
  children: React.ReactNode;
  
  /**
   * Optional content to render if user doesn't have required role.
   * If not provided, nothing is rendered.
   */
  fallback?: React.ReactNode;
  
  /**
   * If true, requires user to have ALL specified roles (AND logic).
   * Default is false (OR logic - user needs at least one role).
   */
  requireAll?: boolean;
}

/**
 * Component that conditionally renders children based on user roles.
 * 
 * @example
 * // Show only to admins
 * <RoleGate allowedRoles={['km-admin']}>
 *   <DeleteButton />
 * </RoleGate>
 * 
 * @example
 * // Show to editors and admins
 * <RoleGate allowedRoles={['km-editor', 'km-admin']}>
 *   <EditButton />
 * </RoleGate>
 * 
 * @example
 * // Show fallback for unauthorized users
 * <RoleGate allowedRoles={['km-admin']} fallback={<Text>Access denied</Text>}>
 *   <AdminPanel />
 * </RoleGate>
 */
export function RoleGate({ 
  allowedRoles, 
  children, 
  fallback = null,
  requireAll = false,
}: RoleGateProps) {
  const { user, hasRole, hasAnyRole } = useAuth();

  // If user is not authenticated, don't show anything
  if (!user) {
    return <>{fallback}</>;
  }

  // Check role requirements
  const hasAccess = requireAll
    ? allowedRoles.every(role => hasRole(role))
    : hasAnyRole(allowedRoles);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Higher-order component version of RoleGate
 */
export function withRoleGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  allowedRoles: UserRole[],
  FallbackComponent?: React.ComponentType<P>
) {
  return function RoleGatedComponent(props: P) {
    return (
      <RoleGate 
        allowedRoles={allowedRoles} 
        fallback={FallbackComponent ? <FallbackComponent {...props} /> : null}
      >
        <WrappedComponent {...props} />
      </RoleGate>
    );
  };
}
